import { useState } from 'react';
import { Button, ConfirmButton, Spinner } from '../ui';
import { useWatchLater, type WatchLaterChoice } from './useWatchLater';
import { WatchLaterPicker } from './WatchLaterPicker';
import { useCopy } from '../strings/useCopy';

/**
 * The one-press Watch later toggle (2026-08-02), in two faces: `corner` — the glyph over a
 * picture, the remove button's clothes, worn by the grid cards and by /embed's top band
 * (2026-08-06) — and `pill`, the watch page's actions-row shape. Both run the same flow: no
 * playlist appointed yet → the picker opens first, and the video is added right after the pick
 * ("if it's not already on there" is the hook's `add`, which the fresh pick makes trivially true to
 * check). Removing confirms, because every removal in this app meets the same two-click grammar.
 *
 * Renders `null` signed out on the corner face: the public grids must not grow a per-card button
 * whose only act would be opening a sign-in dialog — and on /embed a sign-in link would navigate
 * the host page's iframe. The pill face stays visible signed out — the watch page's row explains
 * itself there, like Save to playlist beside it.
 */
export function WatchLaterButton({
  videoId,
  videoTitle,
  face,
  cornerVariant = 'media',
  cornerSize = 'sm',
  tabIndex,
  suppressForPlaylistId,
  onPickerOpenChange,
}: {
  readonly videoId: string;
  /** For the corner face's accessible name — which video, like the remove button says. */
  readonly videoTitle?: string;
  readonly face: 'corner' | 'pill';
  /**
   * The corner face's Button variant: `media` over a thumbnail behind a scrim (history, the feed
   * grids), `ghost` where the row's own remove is a ghost on the page surface (playlist entries).
   */
  readonly cornerVariant?: 'media' | 'ghost';
  /**
   * The corner face's Button size. `sm` in the grids, where the glyph is one of two stacked in a
   * card's corner; `md` on /embed, where the band it rides in *is* player chrome and the bar below
   * it draws 36 px controls — the same square the pointer floor asks for (`button.module.css`).
   */
  readonly cornerSize?: 'sm' | 'md';
  readonly tabIndex?: number;
  /**
   * The playlist page passes its own id: on the appointed list itself the entry's remove button
   * already is "remove from Watch later", and a second button saying the same thing below it would
   * be the duplicate this hides.
   */
  readonly suppressForPlaylistId?: string;
  /**
   * The picker opened or closed. /embed is the caller that needs to know: its button rides in the
   * player's own top band, which fades and goes `inert` on the chrome's clock, and that clock can
   * run out while the dialog is up. The dialog survives it — the band pins itself so that the
   * *button* does too, since closing hands focus back to whatever opened it (`screens/embed/view.tsx`).
   */
  readonly onPickerOpenChange?: (open: boolean) => void;
}) {
  const watchLater = useWatchLater();
  const copy = useCopy().text;
  const words = copy.playlists.watchLater;
  const confirmWord = copy.shell.confirm;
  const [pickerOpen, setPickerOpen] = useState(false);

  const showPicker = (open: boolean): void => {
    setPickerOpen(open);
    onPickerOpenChange?.(open);
  };

  if (face === 'corner' && !watchLater.signedIn) return null;
  if (
    suppressForPlaylistId !== undefined &&
    watchLater.choice !== null &&
    watchLater.choice.playlistId === suppressForPlaylistId
  ) {
    return null;
  }

  const state = watchLater.stateOf(videoId);
  const busy = watchLater.busyOf(videoId);
  const named = videoTitle ?? videoId;
  // 'unknown' only blocks the press while an appointed list is still loading — with none appointed
  // the press opens the picker, which needs no membership answer at all.
  const awaitingList = watchLater.signedIn && watchLater.choice !== null && state === 'unknown';

  const press = (): void => {
    if (!watchLater.signedIn || watchLater.choice === null) {
      showPicker(true);
      return;
    }
    void watchLater.add(videoId, watchLater.choice.playlistId);
  };

  const picked = (choice: WatchLaterChoice): void => {
    showPicker(false);
    void watchLater.add(videoId, choice.playlistId);
  };

  const picker = (
    <WatchLaterPicker
      open={pickerOpen}
      current={watchLater.choice}
      signedIn={watchLater.signedIn}
      onClose={() => showPicker(false)}
      onPicked={picked}
    />
  );

  if (face === 'corner') {
    return (
      <>
        {/*
         * The hover word is the short act — the same sentence the watch page's pill uses,
         * not the accessible name: the name says *which* video because a screen reader has no card
         * to look at, while a tooltip pops up over the card that is already showing the title.
         */}
        {state === 'in' ? (
          <ConfirmButton
            size={cornerSize}
            variant={cornerVariant}
            icon
            tabIndex={tabIndex}
            label={busy ? words.working : words.remove(named)}
            title={busy ? words.working : words.removeTitle}
            confirmLabel={confirmWord}
            confirmTitle={words.remove(named)}
            disabled={busy}
            onConfirm={() => void watchLater.remove(videoId)}
          >
            {busy ? <Spinner /> : <ClockCheckGlyph />}
          </ConfirmButton>
        ) : (
          <Button
            size={cornerSize}
            variant={cornerVariant}
            icon
            tabIndex={tabIndex}
            label={busy ? words.working : words.add(named)}
            title={busy ? words.working : words.addTitle}
            disabled={busy || awaitingList}
            onClick={press}
          >
            {busy ? <Spinner /> : <ClockGlyph />}
          </Button>
        )}
        {picker}
      </>
    );
  }

  return (
    <>
      {state === 'in' ? (
        <ConfirmButton
          size="sm"
          variant="pill"
          label={words.removeTitle}
          title={words.removeTitle}
          confirmLabel={confirmWord}
          confirmTitle={words.removeTitle}
          disabled={busy}
          onConfirm={() => void watchLater.remove(videoId)}
        >
          {busy ? <Spinner /> : <ClockCheckGlyph />}
          {words.inPill}
        </ConfirmButton>
      ) : (
        <Button
          size="sm"
          variant="pill"
          label={words.addTitle}
          title={words.addTitle}
          disabled={busy || awaitingList}
          onClick={press}
        >
          {busy ? <Spinner /> : <ClockGlyph />}
          {words.pill}
        </Button>
      )}
      {picker}
    </>
  );
}

/** A clock in the app's 24-grid stroke-1.8 hand — the Watch later verb, drawn where it is used. */
function ClockGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 8v4.2l2.8 1.7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The same clock wearing the saved state: the face's hands give way to a check. */
function ClockCheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="m8.8 12.2 2.2 2.2 4.2-4.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
